/**
 * Compile a `FlythroughPlaylist` into a time-addressable camera path, and
 * sample it.
 *
 * `compileTour` is called once when playback starts (and by the UI for its
 * speed readout); `sampleTour` is called once per animation frame and must not
 * allocate beyond the returned pose.
 *
 * Coordinate space: control points are converted to normalized Mercator, NOT
 * kept as raw lng/lat. At KAGS (33.4 N) a degree of longitude spans ~0.835x the
 * ground distance of a degree of latitude, so a spline fit to raw degrees is
 * anisotropically stretched — its curvature, and therefore the shape of the
 * camera's path, is simply wrong. Mercator is conformal, so local shape is
 * preserved.
 *
 * Stated assumption: all waypoints of one playlist lie within a single airfield
 * (a few km) and never cross the antimeridian. `SPAN_GUARD` catches a violation
 * and degrades that segment to a straight line rather than producing nonsense.
 */
import mapboxgl from 'mapbox-gl'
import {
  ARC_SAMPLES,
  angleDelta,
  arcLengthToU,
  catmullRom1D,
  catmullRom2D,
  clamp,
  sampleTable,
  unrollBearings,
  warp,
  wrap360,
  wrapIndex,
} from './spline'
import type {
  CameraPose,
  CompiledRun,
  CompiledSegment,
  CompiledTour,
  FlythroughPlaylist,
  TimelineEntry,
} from './types'

/** Mercator-unit span above which we assume bad data and fall back to linear. */
const SPAN_GUARD = 0.5

/** Mapbox hard limits — Catmull-Rom overshoots, so clamp after evaluation. */
const ZOOM_MIN = 0
const ZOOM_MAX = 22
const PITCH_MIN = 0
const PITCH_MAX = 85

/** Largest ramp fraction; beyond ~0.5 the trapezoid has no constant-speed middle. */
const MAX_RAMP_FRACTION = 0.49

/**
 * Reflect an endpoint instead of duplicating it: P(-1) = 2*P0 - P1.
 *
 * Duplicating the endpoint (the textbook trick) produces a zero-length knot
 * interval, which under centripetal parameterization divides by zero. Reflection
 * keeps the knots non-degenerate and yields a natural straight lead-in.
 */
function controlPoint(
  pts: Float64Array,
  n: number,
  loop: boolean,
  i: number
): [number, number] {
  if (loop) {
    const k = wrapIndex(i, n)
    return [pts[k * 2], pts[k * 2 + 1]]
  }
  if (i < 0) return [2 * pts[0] - pts[2], 2 * pts[1] - pts[3]]
  if (i > n - 1) {
    const l = n - 1
    const p = n - 2
    return [2 * pts[l * 2] - pts[p * 2], 2 * pts[l * 2 + 1] - pts[p * 2 + 1]]
  }
  return [pts[i * 2], pts[i * 2 + 1]]
}

/** Scalar equivalent of `controlPoint`, with per-lap winding for bearings. */
function controlScalar(
  vals: Float64Array,
  n: number,
  loop: boolean,
  i: number,
  winding: number
): number {
  if (loop) {
    return vals[wrapIndex(i, n)] + Math.floor(i / n) * winding
  }
  if (i < 0) return 2 * vals[0] - vals[1]
  if (i > n - 1) return 2 * vals[n - 1] - vals[n - 2]
  return vals[i]
}

/**
 * Evaluate a scalar channel (zoom / pitch / unrolled bearing) of segment `s`.
 *
 * `at` is a plain 0..1 progress through the segment — for zoom/pitch/bearing
 * that is the TIME fraction, deliberately not the arc-length parameter used for
 * position. Zoom is already logarithmic, so linear-in-time zoom is what reads as
 * an even zoom; tying it to distance travelled would make it lurch.
 */
function scalarAt(
  tour: CompiledTour,
  s: number,
  at: number,
  vals: Float64Array,
  winding: number
): number {
  const n = tour.zooms.length
  const i = tour.segments[s]?.from ?? s
  return catmullRom1D(
    controlScalar(vals, n, tour.loop, i - 1, winding),
    controlScalar(vals, n, tour.loop, i, winding),
    controlScalar(vals, n, tour.loop, i + 1, winding),
    controlScalar(vals, n, tour.loop, i + 2, winding),
    at
  )
}

/** Evaluate the positional spline of segment `s` at u in [0,1]. */
function positionAt(tour: CompiledTour, s: number, u: number): [number, number] {
  const n = tour.zooms.length
  const seg = tour.segments[s]
  const i = seg.from
  if (seg.degenerate) {
    const a = controlPoint(tour.pts, n, tour.loop, i)
    const b = controlPoint(tour.pts, n, tour.loop, i + 1)
    return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u]
  }
  return catmullRom2D(
    controlPoint(tour.pts, n, tour.loop, i - 1),
    controlPoint(tour.pts, n, tour.loop, i),
    controlPoint(tour.pts, n, tour.loop, i + 1),
    controlPoint(tour.pts, n, tour.loop, i + 2),
    u
  )
}

/**
 * Coerce a possibly-garbage number to something safe.
 *
 * Waypoint fields come from `<input type="number">` and from imported JSON, so a
 * NaN is entirely reachable (`Math.max(100, Number('abc'))` is NaN, not 100).
 * A single NaN duration poisons `totalMs`, and `clock >= NaN` is false forever —
 * the tour then never reaches its end AND throws inside Mapbox when the NaN
 * reaches `MercatorCoordinate`. Sanitizing here keeps that class of bad input
 * from producing an unstoppable playback.
 */
const finite = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback

/**
 * Build the compiled tour. Returns null for a playlist that cannot be flown
 * (fewer than two waypoints, or a total duration of zero).
 */
export function compileTour(playlist: FlythroughPlaylist): CompiledTour | null {
  const raw = playlist.waypoints
  const n = raw.length
  if (n < 2) return null

  // Normalize every numeric field up front so nothing downstream has to.
  const wps = raw.map((w) => ({
    ...w,
    center: [finite(w.center?.[0], 0), finite(w.center?.[1], 0)] as [number, number],
    zoom: clamp(finite(w.zoom, 16), ZOOM_MIN, ZOOM_MAX),
    pitch: clamp(finite(w.pitch, 0), PITCH_MIN, PITCH_MAX),
    bearing: finite(w.bearing, 0),
    durationMs: Math.max(0, finite(w.durationMs, 3000)),
    holdMs: Math.max(0, finite(w.holdMs, 0)),
  }))

  const loop = playlist.loop

  // ── Channels ──────────────────────────────────────────────────────────────
  const pts = new Float64Array(n * 2)
  const zooms = new Float64Array(n)
  const pitches = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    const m = mapboxgl.MercatorCoordinate.fromLngLat(wps[i].center, 0)
    pts[i * 2] = m.x
    pts[i * 2 + 1] = m.y
    zooms[i] = wps[i].zoom
    pitches[i] = wps[i].pitch
  }

  const bearings = unrollBearings(wps.map((w) => w.bearing))
  // Net rotation per lap: the unrolled span plus the closing shortest-arc hop.
  // Non-zero is correct and wanted — an orbit tour should keep turning the same
  // way rather than snapping back at the seam.
  const windingDeg = loop
    ? bearings[n - 1] - bearings[0] + angleDelta(wps[n - 1].bearing, wps[0].bearing)
    : 0

  // ── Segments + arc-length tables ──────────────────────────────────────────
  const segCount = loop ? n : n - 1
  const segments: CompiledSegment[] = []

  // Metres per Mercator unit at the tour's mean latitude — used only for the
  // human-facing speed readout and "Even pace", never for the geometry.
  const meanLat = wps.reduce((acc, w) => acc + w.center[1], 0) / n
  const mercPerMeter = mapboxgl.MercatorCoordinate.fromLngLat([wps[0].center[0], meanLat], 0)
    .meterInMercatorCoordinateUnits()

  const draft: CompiledTour = {
    pts, zooms, pitches, bearings, windingDeg, loop,
    segments, timeline: [], runs: [], totalMs: 0,
  }

  for (let s = 0; s < segCount; s++) {
    const a = controlPoint(pts, n, loop, s)
    const b = controlPoint(pts, n, loop, s + 1)
    const degenerate = Math.abs(b[0] - a[0]) > SPAN_GUARD || Math.abs(b[1] - a[1]) > SPAN_GUARD
    if (degenerate) {
      console.warn(
        `[flythrough] segment ${s} spans an implausible distance — falling back to a straight line. ` +
        `Waypoints are expected to be within one airfield.`
      )
    }

    const seg: CompiledSegment = {
      from: s,
      durationMs: Math.max(0, wps[s].durationMs),
      arc: new Float64Array(ARC_SAMPLES + 1),
      groundByTime: new Float64Array(ARC_SAMPLES + 1),
      lengthM: 0,
      degenerate,
    }
    segments.push(seg)

    // `arc` — cumulative GROUND distance over a uniform spline parameter. Used
    // only to invert "how far along the curve" -> "spline parameter".
    let prev = positionAt(draft, s, 0)
    let accGround = 0
    seg.arc[0] = 0
    for (let k = 1; k <= ARC_SAMPLES; k++) {
      const cur = positionAt(draft, s, k / ARC_SAMPLES)
      accGround += Math.hypot(cur[0] - prev[0], cur[1] - prev[1])
      seg.arc[k] = accGround
      prev = cur
    }
    seg.lengthM = mercPerMeter > 0 ? accGround / mercPerMeter : 0

    // `groundByTime` — the schedule that makes APPARENT pan speed constant.
    //
    // Advancing the camera by equal GROUND distance per unit time is what makes
    // a zoom-in read as "zoom first, then pan": one ground unit is a couple of
    // pixels while wide and hundreds once tight, so the pan is invisible early
    // and stampedes at the end. The eye judges pixels, so ground distance per
    // unit time must instead be proportional to 1 / 2^zoom.
    //
    // Zoom is a pure function of TIME here (see `sampleTour`), never of position,
    // so this schedule can be integrated directly on a uniform time grid — no
    // circular dependency between "where we are" and "how fast we should move".
    const weights = new Float64Array(ARC_SAMPLES + 1)
    for (let k = 0; k <= ARC_SAMPLES; k++) {
      const f = k / ARC_SAMPLES
      weights[k] = 1 / Math.pow(2, scalarAt(draft, s, f, zooms, 0))
    }
    let accW = 0
    seg.groundByTime[0] = 0
    for (let k = 1; k <= ARC_SAMPLES; k++) {
      accW += (weights[k - 1] + weights[k]) / 2 // trapezoid
      seg.groundByTime[k] = accW
    }
    // Normalize to a 0..1 fraction of the segment's ground length.
    if (accW > 0) {
      for (let k = 0; k <= ARC_SAMPLES; k++) seg.groundByTime[k] /= accW
    }
  }

  // ── Timeline: interleave holds and segments ───────────────────────────────
  const timeline: TimelineEntry[] = []
  let cursor = 0
  let runIndex = -1
  let inRun = false
  const runSpans: { startMs: number; endMs: number }[] = []

  const closeRun = () => {
    if (inRun) {
      runSpans[runIndex].endMs = cursor
      inRun = false
    }
  }

  for (let s = 0; s < segCount; s++) {
    const holdMs = Math.max(0, wps[s].holdMs)
    if (holdMs > 0) {
      closeRun()
      timeline.push({ kind: 'hold', wpIndex: s, startMs: cursor, ms: holdMs })
      cursor += holdMs
    }
    const segMs = segments[s].durationMs
    if (segMs <= 0) continue
    if (!inRun) {
      runIndex += 1
      runSpans.push({ startMs: cursor, endMs: cursor })
      inRun = true
    }
    timeline.push({ kind: 'seg', segIndex: s, startMs: cursor, ms: segMs, runIndex })
    cursor += segMs
  }

  // A hold authored on the final waypoint of an open tour still reads as a
  // "hold the last shot" instruction, so honour it.
  if (!loop) {
    const tailHold = Math.max(0, wps[n - 1].holdMs)
    if (tailHold > 0) {
      closeRun()
      timeline.push({ kind: 'hold', wpIndex: n - 1, startMs: cursor, ms: tailHold })
      cursor += tailHold
    }
  }
  closeRun()

  // Belt and braces: a non-finite or empty timeline must never reach playback,
  // where `clock >= totalMs` would be false forever.
  if (!Number.isFinite(cursor) || cursor <= 0) return null

  // ── Ramps ─────────────────────────────────────────────────────────────────
  // A single closed run (looping, no holds) must not ramp: easing to a stop at
  // the seam is exactly the hitch this engine exists to avoid.
  const singleClosedRun = loop && runSpans.length === 1 && !timeline.some((e) => e.kind === 'hold')
  const runs: CompiledRun[] = runSpans.map((span) => {
    const durationMs = span.endMs - span.startMs
    const R = singleClosedRun || durationMs <= 0
      ? 0
      : Math.min(MAX_RAMP_FRACTION, Math.max(0, playlist.rampMs) / durationMs)
    return { startMs: span.startMs, durationMs, R }
  })

  draft.timeline = timeline
  draft.runs = runs
  draft.totalMs = cursor
  return draft
}

/** Pose of a single waypoint, used for holds and previews. */
function poseAtWaypoint(tour: CompiledTour, i: number): CameraPose {
  const ll = new mapboxgl.MercatorCoordinate(tour.pts[i * 2], tour.pts[i * 2 + 1], 0).toLngLat()
  return {
    center: [ll.lng, ll.lat],
    zoom: clamp(tour.zooms[i], ZOOM_MIN, ZOOM_MAX),
    pitch: clamp(tour.pitches[i], PITCH_MIN, PITCH_MAX),
    bearing: wrap360(tour.bearings[i]),
  }
}

/**
 * Sample the tour at `tMs`.
 *
 * Time is clamped to [0, totalMs]; callers handle looping by taking `tMs % totalMs`
 * before calling.
 */
export function sampleTour(tour: CompiledTour, tMs: number): CameraPose {
  const t = clamp(tMs, 0, tour.totalMs)

  // Locate the timeline entry. Entries are contiguous and ordered, so a linear
  // scan over a handful of shots is cheaper than a binary search.
  let entry: TimelineEntry | null = null
  for (let i = 0; i < tour.timeline.length; i++) {
    const e = tour.timeline[i]
    if (t >= e.startMs && t < e.startMs + e.ms) {
      entry = e
      break
    }
  }
  if (!entry) entry = tour.timeline[tour.timeline.length - 1] ?? null
  if (!entry) return poseAtWaypoint(tour, 0)

  if (entry.kind === 'hold') return poseAtWaypoint(tour, entry.wpIndex)

  const run = tour.runs[entry.runIndex]

  // Progress through the RUN (not the segment) drives the ramp, so the ease
  // spans the whole continuous move rather than restarting at each waypoint.
  const runProgress = run && run.durationMs > 0
    ? clamp((t - run.startMs) / run.durationMs, 0, 1)
    : 0
  // Warping remaps time WITHIN the run, so it can move us into a different
  // segment than the one real time landed in (during the ramp-up, virtual time
  // lags real time). Re-resolve the segment against the warped clock — using
  // `entry` directly here would clamp `f` at the segment edges and stutter.
  const warpedRunMs = run ? warp(runProgress, run.R) * run.durationMs : 0

  let active = entry
  if (run) {
    for (let i = 0; i < tour.timeline.length; i++) {
      const e = tour.timeline[i]
      if (e.kind !== 'seg' || e.runIndex !== entry.runIndex) continue
      const offset = e.startMs - run.startMs
      if (warpedRunMs >= offset && warpedRunMs < offset + e.ms) {
        active = e
        break
      }
      // Past the end of the run — stay on its final segment.
      if (warpedRunMs >= offset) active = e
    }
  }

  const seg = tour.segments[active.segIndex]
  const localMs = run ? warpedRunMs - (active.startMs - run.startMs) : 0
  const f = seg.durationMs > 0 ? clamp(localMs / seg.durationMs, 0, 1) : 1

  // POSITION: time fraction -> ground fraction (scale-compensated schedule) ->
  // spline parameter. Two lookups rather than one, so the apparent pan rate stays
  // even regardless of how the zoom changes underneath it.
  const groundFraction = seg.degenerate ? f : sampleTable(seg.groundByTime, f)
  const u = seg.degenerate ? f : arcLengthToU(seg.arc, groundFraction)
  const [mx, my] = positionAt(tour, active.segIndex, u)
  const ll = new mapboxgl.MercatorCoordinate(mx, my, 0).toLngLat()

  // SCALARS ride plain time. Keeping these two parameterizations separate is the
  // whole point: share one and the move degenerates into "zoom, then pan" (or,
  // if you tie zoom to distance instead, "pan, then zoom"). On separate clocks
  // both run the full span of the segment simultaneously.
  return {
    center: [ll.lng, ll.lat],
    zoom: clamp(scalarAt(tour, active.segIndex, f, tour.zooms, 0), ZOOM_MIN, ZOOM_MAX),
    pitch: clamp(scalarAt(tour, active.segIndex, f, tour.pitches, 0), PITCH_MIN, PITCH_MAX),
    bearing: wrap360(scalarAt(tour, active.segIndex, f, tour.bearings, tour.windingDeg)),
  }
}

/** Every pose field finite? A NaN reaching `map.jumpTo` throws and kills the map. */
export function isPoseFinite(p: CameraPose): boolean {
  return (
    Number.isFinite(p.center[0]) &&
    Number.isFinite(p.center[1]) &&
    Number.isFinite(p.zoom) &&
    Number.isFinite(p.pitch) &&
    Number.isFinite(p.bearing)
  )
}

/**
 * Durations that give every segment the same speed, preserving the total.
 *
 * Speed is continuous across a waypoint only when each segment's
 * length/duration ratio matches its neighbour's. Per-waypoint durations are the
 * authoring control we want, so rather than hide the resulting speed steps we
 * surface them and offer this one-click fix.
 *
 * Returns one duration per waypoint, aligned to `playlist.waypoints`.
 */
export function evenPaceDurations(playlist: FlythroughPlaylist): number[] | null {
  const tour = compileTour(playlist)
  if (!tour) return null

  const totalMs = tour.segments.reduce((acc, s) => acc + s.durationMs, 0)
  const totalM = tour.segments.reduce((acc, s) => acc + s.lengthM, 0)
  if (totalMs <= 0 || totalM <= 0) return null

  const out = playlist.waypoints.map((w) => w.durationMs)
  for (const seg of tour.segments) {
    out[seg.from] = Math.round((seg.lengthM / totalM) * totalMs)
  }
  return out
}

/** Ground speed of a segment in mph — the waypoint row's pacing readout. */
export function segmentSpeedMph(seg: CompiledSegment): number {
  if (seg.durationMs <= 0) return 0
  const metersPerSecond = seg.lengthM / (seg.durationMs / 1000)
  return metersPerSecond * 2.236936
}
